/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { HTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import { animated, useSpring } from '@react-spring/web';
import { semiBoldText } from '../utils/fonts';
import { PortfolioCurrency } from '../apollo';
import { getColor, getUpMedia } from '../styles';
import { usePortfolioCurrency } from '../providers';
import { Button } from './buttons';

export type PortfolioCurrencySwitcherProps = HTMLAttributes<HTMLDivElement> & {
  hidden?: boolean;
};

const PortfolioCurrencySwitcher = ({
  hidden = false,
  ...props
}: PortfolioCurrencySwitcherProps) => {
  const { currency, setCurrency } = usePortfolioCurrency();

  const animation = useSpring({
    to: {
      opacity: hidden ? 0 : 1,
    },
  });

  return (
    <Wrapper style={animation} $hidden={hidden} {...props}>
      <CurrencyButton
        $active={currency === PortfolioCurrency.Eur}
        variant={'text'}
        onClick={() => setCurrency(PortfolioCurrency.Eur)}
      >
        EUR
      </CurrencyButton>{' '}
      |{' '}
      <CurrencyButton
        $active={currency === PortfolioCurrency.Usd}
        variant={'text'}
        onClick={() => setCurrency(PortfolioCurrency.Usd)}
      >
        USD
      </CurrencyButton>
    </Wrapper>
  );
};

const Wrapper = styled(animated.div)<{
  $hidden: boolean;
}>`
  pointer-events: ${({ $hidden }) => ($hidden ? 'none' : 'auto')};
  font-size: 1.1rem;
  line-height: 1;
  ${semiBoldText};
  color: ${getColor('darkBlue')};
`;

// noinspection CssUnusedSymbol
const CurrencyButton = styled(Button)<{
  $active: boolean;
}>`
  display: inline-block;
  width: auto;

  /*noinspection CssUnusedSymbol*/
  .MuiButton-root {
    min-width: auto;
    padding: 0;
    position: relative;
    bottom: 1px;

    ${semiBoldText};
    color: ${getColor('darkBlue')};

    &:hover {
      background-color: transparent;
    }

    ${getUpMedia('md')} {
      font-size: inherit;
    }
  }

  ${({ $active }) =>
    $active &&
    css`
      /*noinspection CssUnusedSymbol*/
      .MuiButton-root {
        color: ${getColor('blue')};
      }
    `};
`;

export { PortfolioCurrencySwitcher };
